import { useAuth } from '@/auth/useAuth';
import { useEquipmentInbox } from '@/features/equipment/hooks/useInbox';
import { usePurchasingInbox } from '@/features/purchasing/hooks/usePurchaseRequests';
import { useUnsentCount } from '@/offline/unsent';

/**
 * Counts the navigation shows beside a label (fleet step 3, 2026-08-30).
 *
 * Keyed by `NavItem.badge` so a nav entry names its badge and the layouts
 * stay ignorant of where the number comes from. Each count reads the SAME
 * query its page uses (equipmentKeys.inbox()) — one poll for the badge and
 * the page, never two — and is fetched only for people who can act on it:
 * a badge for a page you cannot open would be noise, and a request that
 * never fires for everyone else.
 *
 * `unsent` (offline reliability §2, 2026-09-14) is the device's own number —
 * outbox rows the server has not acknowledged plus unsynced drafts — read
 * live from IndexedDB, no request at all. It sits on Tools › Sync on a desk
 * and on the More tab on a phone.
 *
 * Zero means "no badge", not "0".
 */
export function useNavBadges(): Record<string, number> {
  const { can } = useAuth();
  const inbox = useEquipmentInbox(can('equipment.approve_moves'));
  // Purchasing (2026-09-25, "Purchasing · 6"): NEW requests waiting for
  // triage — the same query the inbox page reads, gated the same way.
  const purchasing = usePurchasingInbox({}, can('purchase-requests.manage'));
  const unsent = useUnsentCount();
  return {
    equipment_inbox: inbox.data?.counts.requested ?? 0,
    purchasing_inbox: purchasing.data?.counts.new ?? 0,
    unsent,
  };
}

/** What a screen reader hears for a badge — the label says what is counted. */
export const NAV_BADGE_LABELS: Record<string, (n: number) => string> = {
  equipment_inbox: (n) => `${n} request${n === 1 ? '' : 's'} waiting`,
  purchasing_inbox: (n) => `${n} purchase request${n === 1 ? '' : 's'} waiting`,
  unsent: (n) => `${n} item${n === 1 ? '' : 's'} waiting to send`,
};
